import { ldsOurTotalFareDisplayNis } from "./ldsVacationPackageDisplayPrice";
import { resolvePublicDisplayPrice } from "./publicDisplayPriceSSOT";

function pickFirstMeaningfulId(candidates) {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const normalized = String(candidate).trim();
    if (normalized !== "") return normalized;
  }
  return null;
}

function normalizeRoomId(room) {
  return pickFirstMeaningfulId([
    room?.id,
    room?.roomId,
    room?.room_id,
    room?.RoomId,
    room?.roomID,
    room?.roomCode,
    room?.RoomCode,
    room?.["@seqNum"],
    room?.seqNum,
  ]);
}

function resolveRoomName(room) {
  return (
    room?.roomName ??
    room?.RoomName ??
    room?.name ??
    room?.roomType ??
    room?.RoomType ??
    null
  );
}

function toFinite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeComparableId(value) {
  return pickFirstMeaningfulId([value]);
}

function resolveAgentRoomFinalPrice(room, explicitAgentRoomFinalPrice) {
  const fare = room?.ourFareDetails?.ourTotalFare ?? null;
  const supplierNet = toFinite(fare?.initialPrice);
  const hrCommission = toFinite(fare?.hrCommission);
  const candidates = {
    explicitAgentRoomFinalPrice: explicitAgentRoomFinalPrice ?? null,
    roomBaseFromSupplierNetPlusHr:
      supplierNet != null && hrCommission != null ? supplierNet + hrCommission : null,
    roomPriceWithCommissionAndCreditCardFee: fare?.priceWithCommissionAndCreditCardFee ?? null,
    roomPriceWithCommission: fare?.priceWithCommission ?? null,
    roomPriceWithCreditCardFee: fare?.priceWithCreditCardFee ?? null,
    roomInitialPrice: fare?.initialPrice ?? null,
    roomFinalPrice: room?.finalPrice ?? null,
    roomPrice: room?.price ?? null,
  };
  const explicit = toFinite(explicitAgentRoomFinalPrice);
  if (explicit != null && explicit > 0) {
    return {
      value: explicit,
      sourceField: "explicitAgentRoomFinalPrice",
      candidates,
    };
  }
  if (supplierNet != null && hrCommission != null) {
    const base = supplierNet + hrCommission;
    if (Number.isFinite(base) && base > 0) {
      return {
        value: base,
        sourceField: "room.ourFareDetails.ourTotalFare.initialPrice+hrCommission",
        candidates,
      };
    }
  }
  const fromOurFare = ldsOurTotalFareDisplayNis(room?.ourFareDetails?.ourTotalFare);
  return {
    value: fromOurFare,
    sourceField: "room.ourFareDetails.ourTotalFare",
    candidates,
  };
}

/**
 * Each public room card shows that room's public price.
 * The selected card may keep the search/snapshot price only when SSOT is still
 * bound to the same room — never overlay the package total onto a different room.
 */
export function resolvePublicRoomCardPriceIls({
  roomPublicFinalPrice,
  isSelectedRoom = false,
  selectedRoomId = null,
  ssotSelectedRoomId = null,
  ssotSelectedRoomPublicPrice = null,
  bindServerPublicPriceToSelection = false,
} = {}) {
  const live = toFinite(roomPublicFinalPrice);
  const livePositive = live != null && live > 0 ? live : null;
  if (!isSelectedRoom || !bindServerPublicPriceToSelection) return livePositive;
  const idsMatch =
    selectedRoomId != null &&
    ssotSelectedRoomId != null &&
    String(selectedRoomId) === String(ssotSelectedRoomId);
  const ssotPrice = toFinite(ssotSelectedRoomPublicPrice);
  if (idsMatch && ssotPrice != null && ssotPrice > 0) return ssotPrice;
  return livePositive;
}

export function publicPackageSsotMatchesRoomDeal({
  ssot,
  dealId = null,
  packageId = null,
} = {}) {
  if (ssot?.status !== "ready") return false;
  const current = [dealId, packageId]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean);
  if (current.length === 0) return false;
  const fromSsot = [ssot?.dealId, ssot?.packageId]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean);
  if (fromSsot.length === 0) return false;
  return fromSsot.some((id) => current.includes(id));
}

export function resolvePublicRoomCardLivePriceIls({
  publicFinalPrice,
} = {}) {
  const pub = toFinite(publicFinalPrice);
  if (pub != null && pub > 0) return pub;
  return null;
}

export function resolvePublicRoomPricingSnapshot({
  selectedRoomId,
  room,
  agentRoomFinalPrice,
  publicSitePricingSettings,
  productType = "vacation_package_room",
  siteId,
  agentId,
  packageId,
  hotelId,
  board,
  occupancySignature,
  currency = "ILS",
  context = {},
}) {
  const roomId = normalizeRoomId(room);
  const normalizedSelectedRoomId = normalizeComparableId(selectedRoomId);
  const normalizedRoomId = normalizeComparableId(roomId);
  const roomName = resolveRoomName(room);
  if (
    normalizedSelectedRoomId == null ||
    normalizedRoomId == null
  ) {
    const out = {
      status: "invalid_room",
      productType,
      siteId: siteId ?? null,
      agentId: agentId ?? null,
      packageId: packageId ?? null,
      hotelId: hotelId ?? null,
      selectedRoomId: selectedRoomId ?? null,
      roomId: roomId ?? null,
      roomName,
      board: board ?? null,
      occupancySignature: occupancySignature ?? null,
      agentFinalPrice: null,
      commissionType: null,
      commissionValue: null,
      commissionAmount: null,
      publicFinalPrice: null,
      currency,
      sourcePriceField: null,
      source: "selected_public_room_price_snapshot",
      fallbackUsed: false,
    };
    return out;
  }
  if (normalizedRoomId !== normalizedSelectedRoomId) {
    const out = {
      status: "invalid_room",
      productType,
      siteId: siteId ?? null,
      agentId: agentId ?? null,
      packageId: packageId ?? null,
      hotelId: hotelId ?? null,
      selectedRoomId,
      roomId,
      roomName,
      board: board ?? null,
      occupancySignature: occupancySignature ?? null,
      agentFinalPrice: null,
      commissionType: null,
      commissionValue: null,
      commissionAmount: null,
      publicFinalPrice: null,
      currency,
      sourcePriceField: "room_id_mismatch",
      source: "selected_public_room_price_snapshot",
      fallbackUsed: false,
    };
    return out;
  }

  const resolvedAgentRoomPrice = resolveAgentRoomFinalPrice(room, agentRoomFinalPrice);
  const normalizedAgentPrice = resolvedAgentRoomPrice.value;
  const resolved = resolvePublicDisplayPrice({
    productType,
    agentFinalPrice: normalizedAgentPrice,
    publicSitePricingSettings,
    quantity: 1,
    currency,
    context,
  });

  const out = {
    status: resolved.status,
    productType,
    siteId: siteId ?? null,
    agentId: agentId ?? null,
    packageId: packageId ?? null,
    hotelId: hotelId ?? null,
    selectedRoomId,
    roomId,
    roomName,
    board: board ?? null,
    occupancySignature: occupancySignature ?? null,
    agentFinalPrice: resolved.agentFinalPrice,
    commissionType: resolved.commissionType,
    commissionValue: resolved.commissionValue,
    commissionAmount: resolved.commissionAmount,
    publicFinalPrice: resolved.publicFinalPrice,
    currency: resolved.currency ?? currency,
    sourcePriceField: resolvedAgentRoomPrice.sourceField,
    source: "selected_public_room_price_snapshot",
    fallbackUsed: false,
  };
  return out;
}

